import { IFlowExecutionPlugin } from "./IFlowExecutionPlugin";
import { AppGlueCorePlugins } from "../../AppGlueCorePlugins";
import { FlowStorageData } from "./FlowStorageData";
import { FlowStorageHistory } from "./FlowStorageHistory";
import { IUserInfo } from "../DataStorage/IUserInfo";
import { AppGlueError } from "../../AppGlueError";
import { FlowStorageHistoryType } from "./FlowStorageHistoryType";
import { FlowState } from "./FlowState";
import { DataStoreQueryCondition, DataStoreQueryConditionType } from "../DataStorage/DataStoreQueryCondition";
import { FlowStorageResumeType } from "./FlowStorageResumeType";
import { FlowExecutionData } from "../../Flow/Engine/FlowExecutionData";
import { FlowExecutionUtilities } from "./FlowExecutionUtilities";

export class FlowExecutionPluginImpl implements IFlowExecutionPlugin {
    static memoryFlows: { [id: string]: FlowStorageData } = {};
    static onServerResume: (d: FlowStorageData) => void;
    static onClientResume: (d: FlowStorageData) => void;
    static onStore: (d: FlowStorageData) => void;
    ds = AppGlueCorePlugins.DataStoreFactory?.getDataStoreByName<FlowStorageData>(FlowStorageData.name)!;

    async saveFlowData(
        user: IUserInfo,
        flowExecutionData: FlowExecutionData,
    ): Promise<{
        storage: FlowStorageData;
        flowExecutionData: FlowExecutionData;
    }> {
        let storageData = new FlowStorageData(
            user,
            flowExecutionData.flowExecutionId,
            flowExecutionData.flowId,
            new Date(),
            new Date(),
            {},
        );

        if (FlowExecutionPluginImpl.memoryFlows[flowExecutionData.flowExecutionId]) {
            storageData = FlowExecutionPluginImpl.memoryFlows[flowExecutionData.flowExecutionId];
        } else {
            if (await this.ds.existsByAlternateId(user, "flowExecutionId", flowExecutionData.flowExecutionId)) {
                storageData = await this.ds.getByAlternateId(
                    user,
                    "flowExecutionId",
                    flowExecutionData.flowExecutionId,
                );
            }
        }

        storageData.flowExecutionData = flowExecutionData.getStorageData();

        if (!storageData.flowExecutionId) throw new AppGlueError("Flow execution id must be set");

        // eslint-disable-next-line no-constant-condition
        if (flowExecutionData.storeInMemory) {
            FlowExecutionPluginImpl.memoryFlows[storageData.flowExecutionId] = storageData;
        } else {
            delete FlowExecutionPluginImpl.memoryFlows[storageData.flowExecutionId];
            this.ds?.store(user, storageData);
        }

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(storageData);

        return { storage: storageData, flowExecutionData: flowExecutionData };
    }

    async saveFlowDataToDatabase(
        user: IUserInfo,
        flowExecutionData: FlowExecutionData,
    ): Promise<{
        storage: FlowStorageData;
        flowExecutionData: FlowExecutionData;
    }> {
        flowExecutionData.storeInMemory = false;

        return await this.saveFlowData(user, flowExecutionData);
    }

    async cancelFlow(user: IUserInfo, flowExecutionId: string, message?: string): Promise<FlowStorageData> {
        let d = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        d.completedDate = new Date();
        d.flowState = FlowState.CANCELED;

        d.history.push(
            new FlowStorageHistory(
                new Date(),
                FlowStorageHistoryType.COMPLETED,
                user.userEmail,
                message ?? "Flow Canceled",
            ),
        );

        await this.ds.store(user, d);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(d);

        return d;
    }

    async completeFlow(user: IUserInfo, flowExecutionId: string, message?: string): Promise<FlowStorageData> {
        let d = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        d.completedDate = new Date();
        d.flowState = FlowState.COMPLETE;

        d.history.push(
            new FlowStorageHistory(
                new Date(),
                FlowStorageHistoryType.COMPLETED,
                user.userEmail,
                message ?? "Flow Completed",
            ),
        );

        await this.ds.store(user, d);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(d);

        return d;
    }

    async completeFlowInError(
        user: IUserInfo,
        flowExecutionId: string,
        errorMessage: string,
        stepName?: string,
        sequenceName?: string,
    ): Promise<FlowStorageData> {
        let d = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        d.completedDate = new Date();

        d.errorDetails = errorMessage;
        d.flowState = FlowState.ERROR;

        if (stepName) {
            d.history.push(
                new FlowStorageHistory(
                    new Date(),
                    FlowStorageHistoryType.ERROR,
                    user.userEmail,
                    "Error in step: " + stepName + ":" + sequenceName,
                ),
            );
        }

        await this.ds.store(user, d);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(d);

        return d;
    }

    async clearFlowData(user: IUserInfo, flowExecutionId: string): Promise<FlowStorageData> {
        let d = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        await this.ds.delete(user, d.id!);

        return d;
    }

    async getFlowData(
        user: IUserInfo,
        flowExecutionId: string,
    ): Promise<{
        storage: FlowStorageData;
        flowExecutionData: FlowExecutionData;
    }> {
        let d = FlowExecutionPluginImpl.memoryFlows[flowExecutionId];
        if (d)
            return {
                storage: d,
                flowExecutionData: FlowExecutionUtilities.getFlowExecutionDataFromStorage(d),
            };

        let storage = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        return {
            storage: storage,
            flowExecutionData: FlowExecutionUtilities.getFlowExecutionDataFromStorage(storage),
        };
    }

    async addHistory(user: IUserInfo, flowExecutionId: string, history: FlowStorageHistory): Promise<FlowStorageData> {
        let d = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        d.history.push(history);
        this.ds.store(user, d);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(d);

        return d;
    }

    async listAllClientFlowResume(user: IUserInfo, onlyCurrent: boolean): Promise<FlowStorageData[]> {
        let resumeList = await this.ds.query(user, [
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "waitingOnResume",
                FlowStorageResumeType.CLIENT,
            ),
        ]);

        return resumeList.filter((d) => {
            if (onlyCurrent) {
                return d.resumeWhen === undefined || d.resumeWhen.getTime() <= new Date().getTime();
            } else {
                return true;
            }
        });
    }

    async listClientFlowResumeForUser(
        user: IUserInfo,
        userEmail: string,
        onlyCurrent: boolean,
    ): Promise<FlowStorageData[]> {
        let resumeList = await this.ds.query(user, [
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "waitingOnResume",
                FlowStorageResumeType.CLIENT,
            ),
        ]);

        return resumeList.filter((d) => {
            if (userEmail !== d.clientResumeUserEmail) return false;
            if (onlyCurrent) {
                return d.resumeWhen === undefined || d.resumeWhen.getTime() <= new Date().getTime();
            } else {
                return true;
            }
        });
    }

    async listMyClientFlowResume(user: IUserInfo, onlyCurrent: boolean): Promise<FlowStorageData[]> {
        let resumeList = await this.ds.query(user, [
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "waitingOnResume",
                FlowStorageResumeType.CLIENT,
            ),
        ]);

        return resumeList.filter((d) => {
            if (user.userEmail !== d.clientResumeUserEmail) return false;
            if (onlyCurrent) {
                return d.resumeWhen === undefined || d.resumeWhen.getTime() <= new Date().getTime();
            } else {
                return true;
            }
        });
    }

    async listServerFlowResume(user: IUserInfo, onlyCurrent: boolean): Promise<FlowStorageData[]> {
        let resumeList = await this.ds.query(user, [
            new DataStoreQueryCondition(
                DataStoreQueryConditionType.Equals,
                "waitingOnResume",
                FlowStorageResumeType.SERVER,
            ),
        ]);

        return resumeList.filter((d) => {
            if (user.userEmail !== d.clientResumeUserEmail) return false;
            if (onlyCurrent) {
                return d.resumeWhen === undefined || d.resumeWhen.getTime() <= new Date().getTime();
            } else {
                return true;
            }
        });
    }

    async reassignFlowResumeOnClient(
        user: IUserInfo,
        flowExecutionId: string,
        toUser: string,
        toClientId?: string,
    ): Promise<FlowStorageData> {
        let d = await this.ds.getByAlternateId(user, "flowExecutionId", flowExecutionId);

        d.clientResumeUserEmail = toUser;
        d.clientResumeClientId = toClientId;

        await this.ds.store(user, d);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(d);

        if (
            (d.resumeWhen === undefined || d.resumeWhen.getTime() <= new Date().getTime()) &&
            FlowExecutionPluginImpl.onClientResume !== undefined
        ) {
            FlowExecutionPluginImpl.onClientResume(d);
        }

        return d;
    }

    async resumeFlowOnClient(
        user: IUserInfo,
        data: FlowExecutionData,
        userEmail?: string,
        clientId?: string,
        resumeWhen?: Date,
    ): Promise<void> {
        let storage = await this.ds.getByAlternateId(user, "flowExecutionId", data.flowExecutionId);

        storage.clientResumeUserEmail = userEmail;
        storage.clientResumeClientId = clientId;
        storage.waitingOnResume = FlowStorageResumeType.CLIENT;
        storage.resumeWhen = resumeWhen;

        storage.flowExecutionData = data.getStorageData();

        await this.ds.store(user, storage);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(storage);

        if (
            (resumeWhen === undefined || resumeWhen.getTime() <= new Date().getTime()) &&
            FlowExecutionPluginImpl.onClientResume !== undefined
        ) {
            FlowExecutionPluginImpl.onClientResume(storage);
        }
    }

    async resumeFlowOnServer(
        user: IUserInfo,
        data: FlowExecutionData,
        fromUserEmail?: string,
        fromClientId?: string,
        resumeWhen?: Date,
    ): Promise<void> {
        let storage = await this.ds.getByAlternateId(user, "flowExecutionId", data.flowExecutionId);

        storage.clientResumeUserEmail = fromUserEmail;
        storage.clientResumeClientId = fromClientId;
        storage.waitingOnResume = FlowStorageResumeType.CLIENT;
        storage.resumeWhen = resumeWhen;

        storage.flowExecutionData = data.getStorageData();

        await this.ds.store(user, storage);

        if (FlowExecutionPluginImpl.onStore) FlowExecutionPluginImpl.onStore(storage);

        if (
            (resumeWhen === undefined || resumeWhen.getTime() <= new Date().getTime()) &&
            FlowExecutionPluginImpl.onServerResume !== undefined
        ) {
            FlowExecutionPluginImpl.onServerResume(storage);
        }
    }
}
